// アイテム一括登録リニューアル (2026-07-22):
// 複数ピンを一括表示する read-only Mapbox プレビュー。
// インポート結果画面で「どこに配置されたか」をその場で目視確認する用途。
//
// MapPreview.tsx (単一ピン) と同じ規約:
// - REACT_APP_MAPBOX_PUBKEY 未設定時は警告 box にフォールバック (build は通る)
// - style は streets-v12
//
// spec: design/cms-item-bulk-import-renewal-spec.md §2-4

import { useEffect, useRef } from 'react';
import mapboxgl from 'mapbox-gl';
import 'mapbox-gl/dist/mapbox-gl.css';

const MAPBOX_TOKEN = process.env.REACT_APP_MAPBOX_PUBKEY ?? '';
if (MAPBOX_TOKEN && !mapboxgl.accessToken) {
    mapboxgl.accessToken = MAPBOX_TOKEN;
}

// マップ非表示 (イベント用) アイテムの pin。禁止標識と同じ「斜線入り」で、
// 通常の赤 pin と一目で区別できるようにする (operations-map と共通の表現)。
const HIDDEN_PIN_COLOR = '#6B7280';

const makeHiddenPinElement = (size = 22): HTMLDivElement => {
    const el = document.createElement('div');
    el.style.width = `${size}px`;
    el.style.height = `${size}px`;
    el.style.filter = 'drop-shadow(0 1px 4px rgba(0,0,0,0.5))';
    el.innerHTML = `
        <svg width="${size}" height="${size}" viewBox="0 0 20 20" xmlns="http://www.w3.org/2000/svg">
            <circle cx="10" cy="10" r="8" fill="${HIDDEN_PIN_COLOR}" stroke="#FFFFFF" stroke-width="2" />
            <line x1="5.2" y1="14.8" x2="14.8" y2="5.2" stroke="#FFFFFF" stroke-width="2.6" stroke-linecap="round" />
        </svg>
    `;
    return el;
};

export interface MultiPin {
    latitude: number,
    longitude: number,
    label?: string,
    /** マップ非表示 (イベント用) アイテム。半透明グレー pin で区別表示 */
    hidden?: boolean
}

export interface MultiPinMapPreviewProps {
    pins: MultiPin[],
    height?: string
}

const DEFAULT_HEIGHT = '320px';

export default function MultiPinMapPreview({ pins, height = DEFAULT_HEIGHT }: MultiPinMapPreviewProps) {
    const containerRef = useRef<HTMLDivElement | null>(null);
    const mapRef = useRef<mapboxgl.Map | null>(null);
    const markersRef = useRef<mapboxgl.Marker[]>([]);

    useEffect(() => {
        if (!MAPBOX_TOKEN) return;
        if (!containerRef.current || mapRef.current) return;

        const valid = pins.filter(p => Number.isFinite(p.latitude) && Number.isFinite(p.longitude));
        if (valid.length === 0) return;

        const map = new mapboxgl.Map({
            container: containerRef.current,
            style: 'mapbox://styles/mapbox/streets-v12',
            center: [valid[0].longitude, valid[0].latitude],
            zoom: 13
        });
        // 拡大縮小 (+/-) と縮尺バー (2026-07-23: 運用マップと操作感を揃える)
        map.addControl(new mapboxgl.NavigationControl({ visualizePitch: false }), 'top-right');
        map.addControl(new mapboxgl.ScaleControl({ maxWidth: 120, unit: 'metric' }), 'bottom-left');
        mapRef.current = map;

        valid.forEach((p) => {
            // マップ非表示 (イベント用) は「半透明 + 色替え」では判別しにくいため、
            // 運用マップと同じ「斜線入り pin」で区別する (2026-07-23 Boss 指摘)。
            const marker = p.hidden ?
                new mapboxgl.Marker({ element: makeHiddenPinElement() }) :
                new mapboxgl.Marker({ color: '#EA4335' });
            marker.setLngLat([p.longitude, p.latitude]);
            if (p.label || p.hidden) {
                const text = `${p.label ?? ''}${p.hidden ? '（マップ非表示）' : ''}`;
                marker.setPopup(new mapboxgl.Popup({ offset: 24 }).setText(text));
            }
            marker.addTo(map);
            markersRef.current.push(marker);
        });

        if (valid.length > 1) {
            const bounds = new mapboxgl.LngLatBounds();
            valid.forEach(p => bounds.extend([p.longitude, p.latitude]));
            map.fitBounds(bounds, { padding: 48, maxZoom: 15, duration: 0 });
        }

        return () => {
            markersRef.current.forEach(m => m.remove());
            markersRef.current = [];
            map.remove();
            mapRef.current = null;
        };
        // pins はモーダル表示時に確定した配列を受け取る前提 (mount 時のみ描画)
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    if (!MAPBOX_TOKEN) {
        return (
            <div
                className='border border-orange-300 bg-orange-50 text-orange-800 text-sm rounded-md p-3 flex items-center gap-2'
                style={{ height }}
            >
                <span className='font-bold'>⚠️ Map disabled</span>
                <span>
                    REACT_APP_MAPBOX_PUBKEY が設定されていません。.env / GitHub Secret (ENV_FILE_PRODUCTION) に追記してください。
                </span>
            </div>
        );
    }

    if (pins.length === 0) return null;

    return (
        <div
            ref={containerRef}
            className='rounded-md overflow-hidden border'
            style={{ height }}
        />
    );
}
